/**
 * Hook for fetching scheduled appointments with pagination and search
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  scheduledAppointmentsService,
  ScheduledAppointment,
  AppointmentSearchFilters,
} from '@/services/features/ScheduledAppointmentsService';
import { usePEClinicIds } from '@/hooks/pe/usePEClinicIds';

interface UseScheduledAppointmentsReturn {
  virtualAppointments: ScheduledAppointment[];
  inPersonAppointments: ScheduledAppointment[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isAdmin: boolean;
  canViewInPerson: boolean;
  // Virtual pagination
  virtualPage: number;
  virtualTotalPages: number;
  virtualTotalCount: number;
  virtualRecordsPerPage: number;
  setVirtualPage: (page: number) => void;
  setVirtualRecordsPerPage: (records: number) => void;
  // In-person pagination
  inPersonPage: number;
  inPersonTotalPages: number;
  inPersonTotalCount: number;
  inPersonRecordsPerPage: number;
  setInPersonPage: (page: number) => void;
  setInPersonRecordsPerPage: (records: number) => void;
  // Search filters
  virtualFilters: AppointmentSearchFilters;
  inPersonFilters: AppointmentSearchFilters;
  setVirtualFilters: (filters: AppointmentSearchFilters) => void;
  setInPersonFilters: (filters: AppointmentSearchFilters) => void;
  searchVirtual: (filters: AppointmentSearchFilters) => void;
  searchInPerson: (filters: AppointmentSearchFilters) => void;
  resetVirtualSearch: () => void;
  resetInPersonSearch: () => void;
}

export const useScheduledAppointments = (): UseScheduledAppointmentsReturn => {
  const { user, hasuraToken } = useAuth();
  const { clinicIds, isLoading: clinicIdsLoading } = usePEClinicIds();
  const [virtualAppointments, setVirtualAppointments] = useState<ScheduledAppointment[]>([]);
  const [inPersonAppointments, setInPersonAppointments] = useState<ScheduledAppointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Virtual pagination state
  const [virtualPage, setVirtualPage] = useState(1);
  const [virtualRecordsPerPage, setVirtualRecordsPerPage] = useState(10);
  const [virtualTotalCount, setVirtualTotalCount] = useState(0);

  // In-person pagination state
  const [inPersonPage, setInPersonPage] = useState(1);
  const [inPersonRecordsPerPage, setInPersonRecordsPerPage] = useState(10);
  const [inPersonTotalCount, setInPersonTotalCount] = useState(0);

  // Search filters state
  const [virtualFilters, setVirtualFilters] = useState<AppointmentSearchFilters>({});
  const [inPersonFilters, setInPersonFilters] = useState<AppointmentSearchFilters>({});

  // EA and ED are admins - can see all appointments and both tabs
  const isAdmin = user?.type === 'EA' || user?.type === 'ED';
  
  // Only admins can view in-person appointments tab
  const canViewInPerson = isAdmin;

  const virtualTotalPages = Math.ceil(virtualTotalCount / virtualRecordsPerPage) || 1;
  const inPersonTotalPages = Math.ceil(inPersonTotalCount / inPersonRecordsPerPage) || 1;

  const fetchAppointments = useCallback(async () => {
    if (!hasuraToken || !user?.id) {
      setIsLoading(false);
      return;
    }

    // Wait for clinic IDs to finish loading before fetching in-person
    if (clinicIdsLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userId = parseInt(user.id, 10);
      const virtualOffset = (virtualPage - 1) * virtualRecordsPerPage;
      const inPersonOffset = (inPersonPage - 1) * inPersonRecordsPerPage;

      // Fetch virtual appointments with filters
      const virtualResult = await scheduledAppointmentsService.getVirtualAppointments(
        hasuraToken,
        userId,
        isAdmin,
        virtualRecordsPerPage,
        virtualOffset,
        virtualFilters
      );
      setVirtualAppointments(virtualResult.appointments);
      setVirtualTotalCount(virtualResult.totalCount);

      // Fetch in-person appointments only for admins, filtered by clinic IDs
      if (isAdmin) {
        const inPersonResult = await scheduledAppointmentsService.getInPersonAppointments(
          hasuraToken,
          userId,
          isAdmin,
          inPersonRecordsPerPage,
          inPersonOffset,
          inPersonFilters,
          clinicIds
        );
        setInPersonAppointments(inPersonResult.appointments);
        setInPersonTotalCount(inPersonResult.totalCount);
      }
    } catch (err) {
      console.error('Error fetching scheduled appointments:', err);
      setError('Failed to load scheduled appointments');
    } finally {
      setIsLoading(false);
    }
  }, [
    hasuraToken, 
    user?.id, 
    isAdmin, 
    virtualPage, 
    virtualRecordsPerPage, 
    inPersonPage, 
    inPersonRecordsPerPage,
    virtualFilters,
    inPersonFilters,
    clinicIds,
    clinicIdsLoading
  ]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Reset to page 1 when records per page changes
  const handleVirtualRecordsPerPageChange = (records: number) => {
    setVirtualRecordsPerPage(records);
    setVirtualPage(1);
  };

  const handleInPersonRecordsPerPageChange = (records: number) => {
    setInPersonRecordsPerPage(records);
    setInPersonPage(1);
  };

  // Search handlers
  const searchVirtual = (filters: AppointmentSearchFilters) => {
    setVirtualFilters(filters);
    setVirtualPage(1); // Reset to first page on new search
  };

  const searchInPerson = (filters: AppointmentSearchFilters) => {
    setInPersonFilters(filters);
    setInPersonPage(1); // Reset to first page on new search
  };

  const resetVirtualSearch = () => {
    setVirtualFilters({});
    setVirtualPage(1);
  };

  const resetInPersonSearch = () => {
    setInPersonFilters({});
    setInPersonPage(1);
  };

  return {
    virtualAppointments,
    inPersonAppointments,
    isLoading,
    error,
    refetch: fetchAppointments,
    isAdmin,
    canViewInPerson,
    // Virtual pagination
    virtualPage,
    virtualTotalPages,
    virtualTotalCount,
    virtualRecordsPerPage,
    setVirtualPage,
    setVirtualRecordsPerPage: handleVirtualRecordsPerPageChange,
    // In-person pagination
    inPersonPage,
    inPersonTotalPages,
    inPersonTotalCount,
    inPersonRecordsPerPage,
    setInPersonPage,
    setInPersonRecordsPerPage: handleInPersonRecordsPerPageChange,
    // Search filters
    virtualFilters,
    inPersonFilters,
    setVirtualFilters,
    setInPersonFilters,
    searchVirtual,
    searchInPerson,
    resetVirtualSearch,
    resetInPersonSearch,
  };
};
